<template>
  <div class="w-full" v-bind="$attrs">
    <!-- Label / description row -->
    <div
      v-if="label || description || $slots.label"
      class="mb-2 flex items-center justify-between"
    >
      <label
        :for="inputId"
        class="text-sm font-medium tracking-[-0.13px] text-white dark:text-white"
      >
        <slot v-if="$slots.label" name="label" v-bind="slotProps" />
        <template v-else>
          {{ label || description }}
          <span v-if="required" class="ml-0.5 text-red-500">*</span>
        </template>
      </label>
      <!-- Inline error shown next to the label -->
      <span
        v-if="displayError && typeof displayError === 'string'"
        class="text-xs text-red-500 dark:text-[#EA1662]"
      >
        <slot v-if="$slots.error" name="error" v-bind="slotProps" />
        <template v-else>{{ displayError }}</template>
      </span>
    </div>

    <!-- Sub-description slot (renders between label and input) -->
    <div v-if="$slots.description" class="mb-1">
      <slot name="description" v-bind="slotProps" />
    </div>

    <!-- Input slot -->
    <div class="relative">
      <slot v-bind="{ error: displayError }" />
    </div>

    <!-- Help text below input -->
    <div
      v-if="help || hint || $slots.help || $slots.hint"
      class="mt-1 text-xs text-[#4B575D] dark:text-[#4B575D]"
    >
      <slot v-if="$slots.help" name="help" v-bind="slotProps" />
      <slot v-else-if="$slots.hint" name="hint" v-bind="slotProps" />
      <template v-else>{{ help || hint }}</template>
    </div>
  </div>
</template>

<script setup>
  defineOptions({ name: "FormGroup" })

  const formErrors = inject("form-errors", null)

  const props = defineProps({
    label: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    help: {
      type: String,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    error: {
      type: [String, Boolean, Array],
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "md",
    },
    eagerValidation: {
      type: Boolean,
      default: false,
    },
  })

  const inputId = ref(useId())

  // Resolve displayed error from prop or injected form-errors context.
  // Any non-null error prop (including false or []) is treated as an explicit
  // override and suppresses injected errors — callers can pass :error="false"
  // to intentionally clear a form-level error for a specific field.
  const displayError = computed(() => {
    if (props.error !== null) {
      if (props.error === false) return null
      if (typeof props.error === "string") return props.error || null
      if (props.error === true) return true
      if (Array.isArray(props.error))
        return props.error.length > 0 ? props.error[0] : null
    }
    if (props.name && formErrors?.value) {
      const found = formErrors.value.find((e) => e.path === props.name)
      return found?.message ?? null
    }
    return null
  })

  const slotProps = computed(() => ({
    error: displayError.value,
    label: props.label,
    name: props.name,
    hint: props.hint,
    description: props.description,
    help: props.help,
  }))

  provide("form-group", {
    error: displayError,
    inputId,
    name: computed(() => props.name),
    size: computed(() => props.size),
    eagerValidation: computed(() => props.eagerValidation),
  })
</script>
